import React, { useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/utils/cn";

import { HStack, Icon } from "../ui";

import StatusActionCounter from "./status-action-counter";

type StatusActionLayout = "timeline" | "details" | "modal" | "prediction";
type Colors = "accent" | "success" | "primary" | "emphasized" | "default";

interface Action extends React.ComponentProps<"button"> {
  iconSrc: string;
  handler: React.MouseEventHandler<Element>;
  isFilledOnActive?: boolean;
  active?: boolean;
  color?: Colors;
  count?: number;
}

interface BaseProps {
  primaryAction: Action;
  secondaryAction?: Action;
  layout: StatusActionLayout;
  ref?: React.Ref<HTMLButtonElement>;
}

type StatusActionGroupProps = BaseProps &
  (
    | { secondaryAction?: undefined }
    | { secondaryAction: Required<Pick<Action, "iconSrc" | "handler">> }
  );

const StatusActionGroup = (props: StatusActionGroupProps) => {
  const { primaryAction, secondaryAction, layout, ref } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  function buildButton({
    active,
    count,
    color = "default",
    handler,
    iconSrc,
    isFilledOnActive,
    ...buttonProps
  }: Action) {
    return (
      <button
        {...buttonProps}
        ref={ref}
        type="button"
        onClick={handler}
        className={cn(
          "flex items-center justify-center gap-x-1 disabled:pointer-events-none",
          {
            "px-1 py-2": layout === "details",
            "p-1": layout === "modal",
            "px-1 py-1 text-xs lg:px-3": layout === "timeline",
            "text-sm! font-medium!": layout === "prediction",
            "w-full": typeof secondaryAction === "undefined",

            // Accent
            "hover:text-secondary-500": !active && color === "accent",
            "text-secondary-500": active && color === "accent",

            // Success
            "hover:text-success-600 dark:hover:text-accent-green":
              !active && color === "success",
            "text-success-600 dark:text-accent-green":
              active && color === "success",

            // Primary
            "hover:text-primary-500 dark:hover:text-primary-400":
              !active && color === "primary",
            "text-primary-500 dark:text-primary-400":
              active && color === "primary",

            "text-gray-900 dark:text-white": !active && color === "emphasized",

            // Default
            "hover:text-gray-900 dark:hover:text-white": color === "default",
          },
        )}
      >
        <Icon
          src={iconSrc}
          className={cn("size-6", {
            "fill-secondary-500 dark:text-secondary-500":
              active && isFilledOnActive && color === "accent",
            "fill-success-600 dark:fill-accent-green":
              active && isFilledOnActive && color === "success",
            "fill-primary-500 dark:fill-primary-400":
              active && color === "primary",
            "size-5!": layout === "prediction",
          })}
        />

        {layout !== "details" && <StatusActionCounter count={count} />}
      </button>
    );
  }

  useLayoutEffect(() => {
    if (layout === "timeline" && containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [layout, containerRef.current]);

  return (
    <HStack
      ref={containerRef}
      alignItems="center"
      justifyContent="center"
      space={layout === "details" ? 1 : undefined}
      style={{ width }}
      className={cn("group rounded-full rtl:space-x-reverse", {
        "text-gray-600": layout !== "modal",
        "w-full px-2 ring-1 ring-gray-300 dark:ring-gray-800":
          layout === "details",
        "bg-transparent text-white/80 group-hover:text-white dark:bg-transparent":
          layout === "modal",
      })}
    >
      {buildButton(primaryAction)}

      {secondaryAction && buildButton(secondaryAction)}
    </HStack>
  );
};

export { StatusActionGroup as default, type StatusActionLayout };
